import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { scanDocumentBatch } from '../services/documentService';
import { formatAiUserFacingError } from '../utils/documentManagementUi';
import { useAuth } from './AuthContext';

const DocumentUploadContext = createContext();

export const useDocumentUpload = () => {
  const context = useContext(DocumentUploadContext);
  if (!context) {
    throw new Error('useDocumentUpload must be used within a DocumentUploadProvider');
  }
  return context;
};

export const DocumentUploadProvider = ({ children }) => {
  const { user } = useAuth();
  const storageKey = user?.uid ? `avisa_draft_queue_${user.uid}` : 'avisa_draft_queue_general';
  const storageModalKey = user?.uid ? `avisa_draft_modal_open_${user.uid}` : 'avisa_draft_modal_open_general';
  const storageIndexKey = user?.uid ? `avisa_draft_index_${user.uid}` : 'avisa_draft_index_general';

  const [isScanning, setIsScanning] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('idle');

  // Draft documents queue state, persisted across page navigations & browser reloads
  const [draftDocuments, setDraftDocumentsState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.warn('Failed to parse draft documents from storage:', e);
    }
    return [];
  });

  // Track whether the confirmation modal should be open (persisted across page navigation)
  const [isDraftReviewOpen, setIsDraftReviewOpenState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageModalKey);
      if (saved !== null) return saved === 'true';
    } catch (e) {}
    // If there are draft documents waiting, default to open
    try {
      const savedQueue = localStorage.getItem(storageKey);
      if (savedQueue) {
        const parsed = JSON.parse(savedQueue);
        if (Array.isArray(parsed) && parsed.length > 0) return true;
      }
    } catch (e) {}
    return false;
  });

  // Track the index of the draft document currently being confirmed
  const [activeDraftIndex, setActiveDraftIndexState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageIndexKey);
      if (saved !== null) {
        const num = parseInt(saved, 10);
        if (Number.isFinite(num) && num >= 0) return num;
      }
    } catch (e) {}
    return 0;
  });

  // Whenever user changes, reload corresponding state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setDraftDocumentsState(parsed);
          const savedModal = localStorage.getItem(storageModalKey);
          setIsDraftReviewOpenState(savedModal !== null ? savedModal === 'true' : parsed.length > 0);
          return;
        }
      }
      setDraftDocumentsState([]);
      setIsDraftReviewOpenState(false);
    } catch (e) {
      setDraftDocumentsState([]);
      setIsDraftReviewOpenState(false);
    }
  }, [storageKey, storageModalKey]);

  const setDraftDocuments = (action) => {
    setDraftDocumentsState((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      const normalized = Array.isArray(next) ? next : [];
      try {
        if (normalized.length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(normalized));
        } else {
          localStorage.removeItem(storageKey);
          localStorage.removeItem(storageModalKey);
          localStorage.removeItem(storageIndexKey);
          setIsDraftReviewOpenState(false);
        }
      } catch (e) {
        console.warn('Failed to sync draft documents to localStorage:', e);
      }
      return normalized;
    });
  };

  const setIsDraftReviewOpen = (action) => {
    setIsDraftReviewOpenState((prev) => {
      const next = typeof action === 'function' ? action(prev) : Boolean(action);
      try {
        localStorage.setItem(storageModalKey, String(next));
      } catch (e) {}
      return next;
    });
  };

  const setActiveDraftIndex = (action) => {
    setActiveDraftIndexState((prev) => {
      const next = typeof action === 'function' ? action(prev) : Number(action) || 0;
      try {
        localStorage.setItem(storageIndexKey, String(next));
      } catch (e) {}
      return next;
    });
  };

  const clearDraftDocuments = () => {
    setDraftDocuments([]);
    setIsDraftReviewOpen(false);
    setActiveDraftIndex(0);
  };

  const updateUploadProgress = (progress = {}) => {
    if (typeof progress.phase === 'string') {
      setUploadPhase(progress.phase);
    }
    if (typeof progress.percent === 'number' && Number.isFinite(progress.percent)) {
      setUploadProgress(Math.max(0, Math.min(100, progress.percent)));
    }
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const uploadDocuments = async (selectedFiles, onComplete) => {
    // 1. Double upload prevention
    if (isScanning) {
      toast.warn('A document scan is already in progress. Please wait.');
      return;
    }
    
    if (selectedFiles.length === 0) return;

    setIsScanning(true);
    setUploadPhase('preparing');
    setUploadProgress(0);
    setUploadingFiles(selectedFiles.map(f => f.name));
    
    const toastId = toast.info('Extracting text & Analyzing document...', { 
      autoClose: false,
      closeButton: false
    });

    try {
      const batchResult = await scanDocumentBatch(selectedFiles, undefined, updateUploadProgress);
      
      toast.dismiss(toastId);
      if (batchResult.uploadMode === 'merged-image-stack') {
        const excludedInfo = batchResult.excludedCount > 0
          ? ` AI excluded ${batchResult.excludedCount} non-relevant image(s).`
          : '';
        toast.success(`Arranged ${batchResult.keptCount || batchResult.sourceCount} image(s) into 1 PDF and started extraction.${excludedInfo}`);
      } else {
        toast.success(`Successfully uploaded ${batchResult.uploadedCount} file(s). Extracting text & Analyzing...`);
      }

      // Automatically append newly drafted documents to queue and mark modal as open
      const newlyDrafted = [];
      if (batchResult && Array.isArray(batchResult.results)) {
        batchResult.results.forEach((res) => {
          if (res && Array.isArray(res.allDocuments)) {
            newlyDrafted.push(...res.allDocuments);
          } else if (res && res.data) {
            newlyDrafted.push(res.data);
          }
        });
      }

      if (newlyDrafted.length > 0) {
        setDraftDocuments((prev) => {
          const current = Array.isArray(prev) ? prev : [];
          const startIndex = current.length;
          const newTagged = newlyDrafted.map((d, i) => ({
            ...d,
            _isDraft: true,
            _draftQueueIndex: startIndex + i,
          }));
          return [...current, ...newTagged];
        });
        setIsDraftReviewOpen(true);
      }
      
      if (onComplete) {
        onComplete(batchResult, newlyDrafted);
      }
      setUploadProgress(100);
      setUploadPhase('processing');
      await wait(450);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(formatAiUserFacingError(err.message || 'Error occurred while scanning'));
      console.error(err);
    } finally {
      setIsScanning(false);
      setUploadingFiles([]);
      setUploadProgress(0);
      setUploadPhase('idle');
    }
  };

  return (
    <DocumentUploadContext.Provider
      value={{
        isScanning,
        uploadingFiles,
        uploadProgress,
        uploadPhase,
        uploadDocuments,
        draftDocuments,
        setDraftDocuments,
        clearDraftDocuments,
        isDraftReviewOpen,
        setIsDraftReviewOpen,
        activeDraftIndex,
        setActiveDraftIndex,
      }}
    >
      {children}
    </DocumentUploadContext.Provider>
  );
};
